import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import WordBar from './WordBar.jsx'

/** What a click on an element opens, when it opens anything. */
const EDITABLE = { title: 'titleText', headline: 'headlineText' }

/**
 * The words on a picture, handled the way a drawing program handles a box: click to select, click
 * the selected one to type in it, drag it to move it.
 *
 * The picture itself is a server render and stays one — what is shown is what will be published,
 * and no browser draws type the way SkiaSharp does.
 *
 * So a drag is answered here rather than there. The frame goes where it was dropped and stays
 * there; nothing is sent until the words are put down, and then everything that moved goes in one
 * request. Sending on every drop meant a second of waiting between one drag and the next, and a
 * frame that snapped back to where it started for the length of it — which reads as a drag that did
 * not take rather than one still travelling.
 *
 * Where a thing sits is the one change a browser can show honestly: it is a rectangle moving. How
 * the words are SET — heavier, larger, another colour — is not, so the bar still asks the server and
 * waits for the picture. It no longer blocks a drag while it does.
 */
export default function DragTheWords({
  image, boxes, design, disabled, busy, onMove, onWrite, onSet, pointedAt, textOf,
}) {
  const { t } = useTranslation()
  const frameRef = useRef(null)

  const [selected, setSelected] = useState(null)
  const [editing, setEditing] = useState(null)
  const [text, setText] = useState('')

  // Where the held frame has travelled, in pixels — percentages here would be read against the
  // frame's own width rather than the picture's, which is a drag that barely moves.
  const [drag, setDrag] = useState(null)
  const moved = useRef(false)

  // Dropped and not yet sent, as the middle of each element. The ref is what the sending reads and
  // the state is what the drawing reads: a flush fired from an unmount handler sees the ref, and a
  // handler that closed over the state would see whatever it held when it was made.
  const [placed, setPlaced] = useState({})
  const placedRef = useRef({})

  // What the last flush sent, so that the answer can replace it rather than both being kept.
  const sent = useRef(null)

  // Everything that has been sent and not changed since. Clicking away from the picture flushes,
  // and closing the sheet flushes again on the way out — so the same drop went twice, wrote two
  // versions, and started two renders racing over one post.
  const gone = useRef({})

  const boxOf = useCallback(
    (element) => (boxes ?? []).find((box) => box.element === element),
    [boxes],
  )

  /**
   * The element's frame: where the server drew it, unless it has been dropped somewhere since.
   * A dropped point is a middle, and an element whose middle would put half of it off the edge is
   * slid back in whole — the same reading the renderer gives it, so the frame does not promise a
   * place the picture will not honour.
   */
  const frameFor = useCallback((element) => {
    const box = boxOf(element)
    const at = placed[element]

    if (!box || !at) return box

    return {
      ...box,
      x: Math.min(Math.max(at.x - (box.width / 2), 0), Math.max(0, 1 - box.width)),
      y: Math.min(Math.max(at.y - (box.height / 2), 0), Math.max(0, 1 - box.height)),
    }
  }, [boxOf, placed])

  /** Everything that moved, in one request. Nothing to send is not a request. */
  const flush = useCallback(() => {
    const points = {}

    // Only what has moved since it was last sent. Compared by identity, so a point dropped again
    // after its request went out is a new point and goes again.
    for (const [element, at] of Object.entries(placedRef.current))
      if (gone.current[element] !== at) points[element] = at

    if (!onMove || Object.keys(points).length === 0) return undefined

    gone.current = { ...gone.current, ...points }
    sent.current = points

    return onMove(points)
  }, [onMove])

  // Fired from the unmount below, where a handler made at mount would hold the first flush there
  // ever was and none of the drops that came after it.
  const flushRef = useRef(flush)
  flushRef.current = flush

  useEffect(() => () => { flushRef.current() }, [])

  // The answer arrived: the server's boxes are the truth again, for everything it was told. A point
  // dropped again while the request was in the air is not among them, and stays.
  useEffect(() => {
    if (!sent.current) return

    const done = sent.current
    sent.current = null

    const next = { ...placedRef.current }
    for (const element of Object.keys(done))
      if (next[element] === done[element]) delete next[element]

    placedRef.current = next
    setPlaced(next)
  }, [boxes])

  // A finding was tapped somewhere else on the sheet. The element it named is picked up here, so
  // the frame and its bar are where the eye already is. Keyed by the tap rather than by the name,
  // so tapping the same finding twice picks it up again instead of doing nothing.
  useEffect(() => {
    if (!pointedAt?.element || disabled) return

    setEditing(null)
    setSelected(pointedAt.element)
  }, [pointedAt, disabled])

  // A click anywhere else puts the box down, the way clicking off a shape deselects it — and that
  // is when what was moved goes.
  useEffect(() => {
    if (!selected || editing) return undefined

    const away = (event) => {
      if (frameRef.current?.contains(event.target)) return

      setSelected(null)
      flushRef.current()
    }

    window.addEventListener('pointerdown', away)
    return () => window.removeEventListener('pointerdown', away)
  }, [editing, selected])

  const down = useCallback((event, element) => {
    // Not `busy`: a picture being redrawn somewhere else is no reason a frame cannot be picked up.
    if (disabled || editing) return

    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)

    moved.current = false
    setDrag({ element, x: 0, y: 0, fromX: event.clientX, fromY: event.clientY })
  }, [disabled, editing])

  const move = useCallback((event) => {
    setDrag((held) => {
      if (!held) return held

      const x = event.clientX - held.fromX
      const y = event.clientY - held.fromY

      // Four pixels of wobble is a click with a shaky hand, not a drag.
      if (Math.abs(x) > 4 || Math.abs(y) > 4) moved.current = true

      return { ...held, x, y }
    })
  }, [])

  const up = useCallback(() => {
    const held = drag
    setDrag(null)

    if (!held) return

    const box = frameFor(held.element)
    const frame = frameRef.current

    if (!box || !frame) return

    if (!moved.current) {
      // The first click picks it up; the next one opens it, which is the order a drawing program
      // taught everybody. The price is never opened: it is the catalogue's number.
      if (selected === held.element && EDITABLE[held.element]) {
        setEditing(held.element)
        setText(textOf?.(held.element) ?? '')
      } else {
        setSelected(held.element)
      }

      return
    }

    const { width, height } = frame.getBoundingClientRect()

    // Where its middle ended up, which is what a dropped point means. Kept here; the server hears
    // about it when the words are put down.
    const at = {
      x: Math.min(Math.max(box.x + (box.width / 2) + (held.x / width), 0), 1),
      y: Math.min(Math.max(box.y + (box.height / 2) + (held.y / height), 0), 1),
    }

    placedRef.current = { ...placedRef.current, [held.element]: at }
    setPlaced(placedRef.current)
    setSelected(held.element)
  }, [drag, frameFor, selected, textOf])

  const save = useCallback(() => {
    const element = editing
    setEditing(null)

    if (element && text.trim() !== (textOf?.(element) ?? '').trim())
      onWrite?.(EDITABLE[element], text.trim())
  }, [editing, onWrite, text, textOf])

  return (
    <div className="framed" ref={frameRef}>
      <img className="mock__image" src={image} alt="" loading="lazy" />

      {(boxes ?? []).map((box) => {
        const at = frameFor(box.element) ?? box
        const held = drag?.element === box.element && moved.current
        const on = selected === box.element
        const open = editing === box.element

        const style = {
          left: `${at.x * 100}%`,
          top: `${at.y * 100}%`,
          width: `${at.width * 100}%`,
          height: `${at.height * 100}%`,
          transform: held ? `translate(${drag.x}px, ${drag.y}px)` : undefined,
        }

        if (open) {
          return (
            <input
              key={box.element}
              className="grip grip--open"
              style={style}
              value={text}
              autoFocus
              maxLength={80}
              onChange={(event) => setText(event.target.value)}
              onBlur={save}
              onKeyDown={(event) => {
                if (event.key === 'Enter') save()

                if (event.key === 'Escape') {
                  // Kept here: the sheet closes on Escape too, so typing a name and changing your
                  // mind about it shut the whole thing.
                  event.stopPropagation()
                  setEditing(null)
                }
              }}
            />
          )
        }

        return (
          <div
            key={box.element}
            role="button"
            tabIndex={disabled ? -1 : 0}
            className={`grip${on ? ' grip--on' : ''}${held ? ' grip--held' : ''}`}
            style={style}
            aria-label={t(`posts.design${box.element === 'title' ? 'Name' : box.element === 'price' ? 'Price' : 'Line'}`)}
            onPointerDown={(event) => down(event, box.element)}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={() => setDrag(null)}
          />
        )
      })}

      {/* Over whatever is held, the way a drawing program puts its levers over the shape you picked
          up rather than in a panel somewhere else. Not while it is travelling: a bar that follows a
          finger is a bar being dragged. */}
      {selected && onSet && !disabled && !drag && frameFor(selected) && (
        <WordBar
          element={selected}
          design={design}
          at={frameFor(selected)}
          busy={busy}
          onSet={(patch) => {
            // Where it sits goes first and on its own: a dropped point belongs to the face it was
            // dropped on, and the weight of a name belongs to all three. One request cannot carry
            // both without one of them being told the other's answer.
            Promise.resolve(flush()).then(() => onSet(selected, patch))
          }}
        />
      )}

      {(boxes ?? []).length > 0 && !disabled && !editing && (
        <span className="framed__hint">
          {t(selected ? 'posts.dragHintSelected' : 'posts.dragHint')}
        </span>
      )}
    </div>
  )
}
